import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useState } from 'react';
import { toast } from 'sonner';
import { useAdminUserSession } from '../../../../models/hooks/adminStateHooks';
import { AdminPermission } from '../../../../viewmodels/AdminViewModels';
import { useRemoveAccountLogoMutation } from '../../../../queries/Account/useRemoveAccountLogoMutation';
import { useUploadAccountLogoMutation } from '../../../../queries/Account/useUploadAccountLogoMutation';
import { invalidateAccountAdminQuery } from '../../../../queries/Admin/Account/useAdminAccountQuery';
import { Button } from '../../../../web/components-v2/Button';
import { Dialog, DialogBody, DialogFooter, DialogPopup, DialogTitle } from '../../../../web/components-v2/Dialog';
import { ImageFileChooserModal } from '../../../../web/components/ImageFileChooserModal';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { accountLogoFieldStyles as s } from './accountLogoFieldStyles';

export interface IAccountLogoFieldProps {
	/** The persisted account; the current logo is read from `account.preferences.logo.url`. */
	account: Api.IAccount;
}

/**
 * Account Overview logo field. No MobX / no viewmodels — reads the current logo straight off
 * the account and drives upload/remove through the existing react-query mutations, invalidating
 * the admin account query on success so the parent refetches the new logo. Read-only display is
 * always shown; the Upload / Edit / Remove affordances are gated behind `ModifyAccount`.
 */
export const AccountLogoField = ({ account }: IAccountLogoFieldProps) => {
	const canModifyAccount = useAdminUserSession().hasPermission(AdminPermission.ModifyAccount);
	const [showFileChooser, setShowFileChooser] = useState(false);
	const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

	const uploadMutation = useUploadAccountLogoMutation({
		onSuccess: () => {
			setShowFileChooser(false);
			invalidateAccountAdminQuery();
		},
		onError: () => {
			setShowFileChooser(false);
			toast.error('Failed to upload the account logo. Please try again.');
		},
	});

	const removeMutation = useRemoveAccountLogoMutation({
		onSuccess: () => {
			setShowRemoveConfirm(false);
			invalidateAccountAdminQuery();
		},
		onError: () => {
			setShowRemoveConfirm(false);
			toast.error('Failed to remove the account logo. Please try again.');
		},
	});

	const logoUrl = account.preferences?.logo?.url;
	const isPending = uploadMutation.isPending || removeMutation.isPending;

	// The logo endpoints are scoped by the impersonation prefix (impersonate/{id}/…); without it the
	// upload/remove hits the admin's own account, not the customer being viewed. Legacy forwards this.
	const impersonationContext: Api.IImpersonationContext = { account: { id: account.id } };

	const onFileSelected = (result?: File, canceled?: boolean) => {
		if (!canceled && result) {
			uploadMutation.mutate({ logo: result, impersonationContext });
		} else {
			setShowFileChooser(false);
		}
	};

	return (
		<div className={css(s.container)}>
			<div className={css(s.labelRow)}>
				<span>Logo</span>
				{canModifyAccount &&
					(isPending ? (
						<LoadingSpinner type='extra-tiny' />
					) : logoUrl ? (
						<span className={css(s.actions)}>
							<Button variant='link' styles={[s.link]} onClick={() => setShowFileChooser(true)}>
								Edit
							</Button>
							<span>|</span>
							<Button variant='link' styles={[s.link]} onClick={() => setShowRemoveConfirm(true)}>
								Remove
							</Button>
						</span>
					) : (
						// Empty state: "Upload Image" sits inline with the LOGO label (like Edit/Remove do).
						<Button variant='link' styles={[s.link]} onClick={() => setShowFileChooser(true)}>
							Upload Image
						</Button>
					))}
			</div>

			{logoUrl && <img className={css(s.logo)} src={logoUrl} alt='Account logo' />}

			{canModifyAccount && (
				<>
					<ImageFileChooserModal
						title='Upload Logo'
						modalProps={{
							isOpen: showFileChooser,
							onRequestClose: onFileSelected,
						}}
					/>

					<Dialog open={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
						<DialogPopup>
							<DialogTitle>Remove Logo</DialogTitle>
							<DialogBody>Are you sure you want to remove the account logo?</DialogBody>
							<DialogFooter>
								<Button
									variant='secondary'
									onClick={() => setShowRemoveConfirm(false)}
									disabled={removeMutation.isPending}
								>
									Cancel
								</Button>
								<Button
									variant='destructive'
									onClick={() => removeMutation.mutate({ impersonationContext })}
									isLoading={removeMutation.isPending}
								>
									Remove Logo
								</Button>
							</DialogFooter>
						</DialogPopup>
					</Dialog>
				</>
			)}
		</div>
	);
};
